import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Box from '../Box';
import useMenuItem from './useMenuItem';
import useTracking, { trackingShape, EVENT_CLICK } from '../hooks/useTracking';

const events = { EVENT_CLICK };

const MenuItem = forwardRef(({
  children,
  active,
  highlighted,
  iconBefore,
  iconAfter,
  tracking,
  onClick,
  style, // don't forward
  className, // don't forward
  ...rest
}, ref) => {
  const { track } = useTracking(tracking, 'MenuItem', events);

  const {
    classNames,
    IconBefore,
    IconAfter,
    Content,
  } = useMenuItem({
    active,
    highlighted,
    iconBefore,
    iconAfter,
  });

  return (
    <Box
      element="button"
      display="flex"
      alignItems="center"
      className={classNames}
      innerRef={ref}
      {...rest}
      onClick={(...args) => {
        track(EVENT_CLICK);
        if (typeof onClick === 'function') onClick(...args);
      }}
    >
      <IconBefore />
      <Content>
        { children }
      </Content>
      <IconAfter />
    </Box>
  );
});

MenuItem.propTypes = {
  /**
    * The text for this menu item
    */
  children: PropTypes.node.isRequired,
  /**
    * Whether or not to display this item as active
    */
  active: PropTypes.bool,
  /**
    * An <Icon /> to show on the right of the item
    */
  iconAfter: PropTypes.node,
  /**
    * An <Icon /> to show on the left of the item
    */
  iconBefore: PropTypes.node,
  /**
    * Tracking configuration for auto tracking
    */
  tracking: trackingShape,
};

MenuItem.defaultProps = {};

MenuItem.displayName = 'MenuItem';

export default MenuItem;
